import { Injectable } from '@angular/core';
import { AbstractTimeoutComponent } from '@nationwide/internet-servicing-angular-components';
import {
    from,
    fromEvent,
    merge,
    Observable,
    Subject,
    Subscription,
    timer
} from 'rxjs';
import { bufferTime, filter, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
@Injectable({
    providedIn: 'root'
})
export class TpiIdleService extends AbstractTimeoutComponent {
    private activityEvents$: Observable<any>;
    private idleEnd$ = new Subject<boolean>();
    private idleSubscription: Subscription = new Subscription();
    private idleTimeout$ = new Subject<boolean>();
    private idleTimeoutWarning$ = new Subject<boolean>();
    private idleWarningTimerSubscription: Subscription = new Subscription();
    private isAuth = true;
    private millisec: number;
    private minutes: number;
    private seconds: number;
    private sessionExpireTimeout$ = new Subject<void>();
    private sessionExpireWarning$ = new Subject<void>();
    private timeoutSubscription?: Subscription;
    private timeoutTime: number;
    private warningSubscription?: Subscription;

    constructor() {
        super();
        this.minutes = environment.idleTimeoutInMinutes;
        this.seconds = 60;
        this.millisec = 1000;
        this.timeoutTime = this.minutes * this.seconds * this.millisec;
    }

    checkTimeoutWarning(): Subscription {
        return timer(0, this.millisec).subscribe(() => {
            if (this.isOpen) {
                this.isOpen = false;
                this.idleTimeoutWarning$.next(true);
                this.idleSubscription.unsubscribe();
                this.idleWarningTimerSubscription.unsubscribe();
            }
        });
    }

    endUserSession(voluntary: boolean): void {
        if (!voluntary) {
            this.idleTimeout$.next(true);
        }
        this.idleWarningTimerSubscription.unsubscribe();
        this.idleSubscription.unsubscribe();
        this.idleEnd$.complete();
        this.idleTimeout$.complete();
        this.idleTimeoutWarning$.complete();
        this.idleEnd$ = new Subject<boolean>();
        this.idleTimeout$ = new Subject<boolean>();
        this.idleTimeoutWarning$ = new Subject<boolean>();
        this.isAuth = false;
    }
    getAuth(): any {
        return this.isAuth;
    }

    getIdleEnd(): Subject<boolean> {
        return this.idleEnd$;
    }

    getIdleTimeout(): Subject<boolean> {
        return this.idleTimeout$;
    }

    getIdleTimeoutWarning(): Subject<boolean> {
        return this.idleTimeoutWarning$;
    }

    onIdleEnd(): Observable<boolean> {
        return this.idleEnd$;
    }

    onSessionExpireTimeout(): Observable<void> {
        return this.sessionExpireTimeout$;
    }

    onSessionExpireWarning(): Observable<void> {
        return this.sessionExpireWarning$;
    }

    onTimeout(): Observable<boolean> {
        return this.idleTimeout$;
    }

    onTimeoutWarning(): Observable<boolean> {
        return this.idleTimeoutWarning$;
    }

    refreshUserSession(): void {
        this.startWatching();
    }

    setActivityEvents(events$: Observable<any>): void {
        this.activityEvents$ = events$;
    }

    setAuth(isAuth: boolean): any {
        this.isAuth = isAuth;
    }

    setIdleSubscription(idleSub: any): void {
        this.idleSubscription = idleSub;
    }

    setIdleWarningSubscription(idleWarnSub: any): void {
        this.idleTimeoutWarning$ = idleWarnSub;
    }

    setIdleWarningTimerSubscription(idleWarningSub: any): any {
        this.idleWarningTimerSubscription = idleWarningSub;
    }

    startSessionExpireWarningTimer(): void {
        const milliseconds = 1000;
        this.warningSubscription?.unsubscribe();
        this.timeoutSubscription?.unsubscribe();

        this.warningSubscription = timer(environment.tokenTimeout * milliseconds).subscribe(() => {
            this.sessionExpireWarning$.next();
        });

        this.timeoutSubscription = timer(environment.tokenExpiry * milliseconds).subscribe(() => {
            this.sessionExpireTimeout$.next();
        });
    }

    startWatching(): any {
        if (!this.activityEvents$) {
            this.activityEvents$ = merge(
                fromEvent(window, 'mousemove'),
                fromEvent(window, 'mousedown'),
                fromEvent(window, 'resize'),
                fromEvent(window, 'scroll'),
                fromEvent(document, 'touchstart'),
                fromEvent(document, 'touchend'),
                fromEvent(document, 'keydown')
            );
        }
        this.activityEvents$ = from(this.activityEvents$);
        if (this.idleWarningTimerSubscription) {
            this.idleWarningTimerSubscription.unsubscribe();
        }
        if (this.idleSubscription) {
            this.idleSubscription.unsubscribe();
        }
        if (this.idleWarningTimerSubscription) {
            this.idleWarningTimerSubscription.unsubscribe();
        }
        const idleSensitivityMillisec = 1000;
        this.timeoutTime = Date.now() + this.minutes * this.seconds * this.millisec;
        this.init();
        this.isOpen = false;
        this.idleWarningTimerSubscription = this.checkTimeoutWarning();
        this.idleSubscription = this.activityEvents$
            .pipe(
                bufferTime(idleSensitivityMillisec),
                filter((event) => event.length > 0),
                tap(() => {
                    this.timeoutTime = Date.now() + this.minutes * this.seconds * this.millisec;
                    this.init();
                    this.isOpen = false;
                    this.idleEnd$.next(true);
                })
            )
            .subscribe();
    }

    tokenExpiration(): number {
        let expire;
        if (this.getAuth()) {
            expire = this.timeoutTime;
        }
        return expire;
    }
}